import { useTranslation } from "react-i18next";
import { RightDrawer } from "./RightDrawer";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Counts and gating used to render badges / disabled rows. */
  orderCount: number;
  hasServices: boolean;
  hasAllergens: boolean;
  /** Handlers — each one closes the drawer in the parent if needed. */
  onOpenLanguage: () => void;
  onOpenAllergen: () => void;
  onOpenService: () => void;
  onOpenHistory: () => void;
  /** Display label for the active language (flag + label). */
  currentLangFlag: string;
  currentLangLabel: string;
  /** Live "info-block" metrics — null when not applicable. */
  remainingTurns?: number | null;
  remainingQuantity?: number | null;
  cooldownRemaining?: string | null;
  /** Faux en mode consultation (showSitInCart) : ni historique ni compteurs. */
  showOrdering?: boolean;
}

const MetricCard = ({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  tone?: "neutral" | "warm";
}): JSX.Element => {
  const cls =
    tone === "warm"
      ? "bg-warm/10 border-warm/40"
      : "bg-surface border-border";
  return (
    <div className={`w-20 flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-button border ${cls}`}>
      <span className="text-[9px] uppercase tracking-wider text-ink-muted leading-tight text-center">
        {label}
      </span>
      <span className="display-font text-sm font-semibold text-ink tabular-nums leading-none whitespace-nowrap">
        {value}
      </span>
    </div>
  );
};

interface Row {
  key: string;
  icon: string;
  label: string;
  hint?: string;
  badge?: string | number;
  onClick: () => void;
  disabled?: boolean;
}

export const MainMenuDrawer = ({
  open,
  onClose,
  orderCount,
  hasServices,
  hasAllergens,
  onOpenLanguage,
  onOpenAllergen,
  onOpenService,
  onOpenHistory,
  currentLangFlag,
  currentLangLabel,
  remainingTurns = null,
  remainingQuantity = null,
  cooldownRemaining = null,
  showOrdering = true,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const hasMetrics =
    showOrdering && (remainingTurns != null || remainingQuantity != null || !!cooldownRemaining);

  const rows: Row[] = [
    {
      key: "language",
      icon: currentLangFlag,
      label: t("modalFlag.selectLanguage", "Choisissez votre langue"),
      hint: currentLangLabel,
      onClick: onOpenLanguage,
    },
    {
      key: "allergen",
      icon: "🌾",
      label: t("header.allergen", "Allergène"),
      onClick: onOpenAllergen,
      disabled: !hasAllergens,
      hint: hasAllergens ? undefined : t("mainMenu.noAllergenInfo", "Aucune information disponible"),
    },
    {
      key: "service",
      icon: "🛎️",
      label: t("header.service", "Service"),
      onClick: onOpenService,
      disabled: !hasServices,
      hint: hasServices ? undefined : t("mainMenu.unavailable", "Indisponible"),
    },
    ...(showOrdering
      ? [
          {
            key: "history",
            icon: "📋",
            label: t("header.history", "Historique"),
            onClick: onOpenHistory,
            badge: orderCount > 0 ? orderCount : undefined,
            disabled: orderCount === 0,
            hint: orderCount === 0 ? t("mainMenu.noOrderYet", "Aucune commande envoyée") : undefined,
          } satisfies Row,
        ]
      : []),
  ];

  const header = (
    <div className="px-2 pt-3 pb-2 border-b border-border flex items-center justify-end pt-safe">
      <button
        type="button"
        onClick={onClose}
        className="w-8 h-8 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink shrink-0"
        aria-label={t("common.close", "Fermer")}
      >
        ✕
      </button>
    </div>
  );

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      ariaLabel={t("mainMenu.ariaLabel", "Menu")}
      header={header}
      widthClass="w-auto"
    >
      {hasMetrics && (
        <section className="px-2 pt-3 flex flex-col gap-2 items-center">
          {remainingTurns != null && (
            <MetricCard
              label={t("header.remainingTurn", "Tour restant")}
              value={remainingTurns}
            />
          )}
          {remainingQuantity != null && (
            <MetricCard
              label={t("header.remainingQuantity", "Quantité restante")}
              value={remainingQuantity}
            />
          )}
          {cooldownRemaining && (
            <MetricCard
              label={t("header.nextOrder", "Prochaine commande")}
              value={cooldownRemaining}
              tone="warm"
            />
          )}
        </section>
      )}

      <ul className="px-2 py-3 flex flex-col gap-2 items-center">
        {rows.map((row) => (
          <li key={row.key}>
            <button
              type="button"
              onClick={row.onClick}
              disabled={row.disabled}
              className="relative w-20 h-20 flex flex-col items-center justify-center gap-1 px-1 py-1 rounded-button border-2 border-border bg-surface hover:border-warm/60 transition-colors disabled:opacity-40 disabled:hover:border-border"
            >
              <span className="text-2xl leading-none" aria-hidden="true">{row.icon}</span>
              <span className="text-ink font-medium text-[11px] text-center leading-tight">
                {row.label}
              </span>
              {row.badge !== undefined && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold">
                  {row.badge}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </RightDrawer>
  );
};
